import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function Widgets() {
  return (
    <DocPage
      eyebrow="Building"
      title="Building widgets with dynamic_widget"
      intro={
        <>
          A widget is a self-contained React component MCPBuild bundles, hosts,
          and renders inline in Claude / ChatGPT. You don't write React — you
          describe what you want and the AI calls <K>dynamic_widget</K> with the
          code. This guide is what to <em>say</em> to make the result land where
          you want it.
        </>
      }
    >
      <DocSection id="anatomy" title="What dynamic_widget actually takes">
        <P>The AI fills these in based on your prompt:</P>
        <UL>
          <li>
            <K>source</K> — React TSX. A single file with a default-exported
            component. Allowed imports: <K>react</K>, <K>react-dom/client</K>,{" "}
            <K>@martzmakes/widget</K>.
          </li>
          <li>
            <K>data</K> — the JSON the widget reads via <K>useToolOutput()</K>{" "}
            at render time. This is the "state" you pass in your prompt.
          </li>
          <li>
            <K>description</K> — a one-sentence label shown in your Inbox.
          </li>
          <li>
            <K>projectSlug</K> / <K>projectName</K> — optional. Save it directly
            into a project instead of the Inbox (more below).
          </li>
          <li>
            <K>toolName</K> / <K>toolDescription</K> / <K>inputSchema</K> —
            optional metadata describing what the widget would look like as a
            reusable tool. Lets the AI hand it off to a project cleanly.
          </li>
        </UL>
      </DocSection>

      <DocSection id="auto-save" title="Inbox or project? The projectName auto-save">
        <P>
          By default, a widget lands in your <strong>Inbox</strong> — a 24-hour
          ephemeral scratch space. Great for iterating. The catch: if you
          intended the widget to live in a project, the Inbox is the wrong
          place. The classic confusion is "I asked for an Orders project but
          there's no widget on the Orders page!" — because the AI dropped the
          widget in your Inbox.
        </P>
        <P>
          Fix: tell the AI to set <K>projectName</K> on the{" "}
          <K>dynamic_widget</K> call. The widget is created in Inbox <em>and</em>{" "}
          saved into the project as a callable tool in the same step.
        </P>
        <PromptCard
          title="Lands in Inbox (default)"
          prompt={`Use dynamic_widget to render a quarterly-revenue bar chart from useToolOutput(). Data: { quarters: ["Q1","Q2","Q3","Q4"], values: [120, 145, 138, 160] }.`}
          note="Use this when iterating on an idea you might throw away."
        />
        <PromptCard
          title="Auto-saves into a project"
          prompt={`Use dynamic_widget with projectName: "Sales" and toolSlug: "quarterly-revenue" to render a bar chart from useToolOutput(). Data: { quarters: ["Q1","Q2","Q3","Q4"], values: [120, 145, 138, 160] }. Description: "Quarterly revenue bar chart".`}
          note={
            <>
              Use this for anything you actually want to keep. The result
              includes <K>savedTo.mcpUrl</K> (e.g.{" "}
              <K>https://mcpbuild.app/mcp/sales</K>) — ready to connect.
            </>
          }
        />
      </DocSection>

      <DocSection id="data" title="How the widget reads your data — useToolOutput()">
        <P>
          Inside the widget code, the data you passed shows up as the return
          value of <K>useToolOutput()</K>. The AI will write something like:
        </P>
        <Code>{`import { useToolOutput } from "@martzmakes/widget";

export default function Widget() {
  const { quarters, values } = useToolOutput();
  // ...render a chart from quarters + values
}`}</Code>
        <Callout kind="warn" title="Don't hardcode the data in the source.">
          Always pass it as the <K>data</K> arg to <K>dynamic_widget</K>. That
          way the same widget can be saved as a reusable project tool whose
          input schema lets callers <em>vary</em> the data on each call.
        </Callout>
      </DocSection>

      <DocSection id="errors" title="When something goes wrong: error visibility">
        <P>
          We render the widget two places — inline in your chat (Claude /
          ChatGPT) and on the project page web preview. If the React throws
          (most often <K>Cannot read properties of undefined (reading 'map')</K>
          when the LLM destructured the data slightly wrong), you'll see the
          actual stack <em>inline in the iframe</em> instead of a blank box.
        </P>
        <P>
          Recovery is one prompt: paste the stack back into your chat and ask
          for a fix. The AI calls <K>remix_widget</K> to rewrite the source —
          version history is kept, so you can always restore the prior version.
        </P>
        <PromptCard
          title="One-shot recovery"
          prompt={`The orders-table widget on my "Orders" project is throwing this:

  TypeError: Cannot read properties of undefined (reading 'map')
    at Widget (orders-table:23:18)

Use remix_widget to fix it. Make rows default to [] so it renders an empty state instead of crashing.`}
        />
      </DocSection>

      <DocSection id="theme" title="Theming + display modes">
        <P>
          Two tiny APIs from <K>@martzmakes/widget</K> make widgets host-aware:
        </P>
        <UL>
          <li>
            <K>useTheme()</K> returns <K>"light"</K> or <K>"dark"</K> based on
            the host's preference. Branch your colors on it.
          </li>
          <li>
            <K>useDisplayMode()</K> returns <K>"inline"</K>, <K>"fullscreen"</K>
            , or <K>"pip"</K>. Tweak layout / typography accordingly.
          </li>
        </UL>
        <P>
          Tell the AI to respect the host theme:
          <em> "use useTheme() — dark mode should use deep purple accents and
          near-black backgrounds."</em> Background should generally be{" "}
          <K>transparent</K> — the host paints the surrounding chrome.
        </P>
      </DocSection>

      <DocSection id="next" title="Next steps">
        <OL>
          <li>
            <a href="/docs/data-tools">Data tools + callTool()</a> — make
            widgets interactive (buttons that do things, refresh patterns).
          </li>
          <li>
            <a href="/docs/triggers">Trigger phrases</a> — make a chat client
            pick your widget when someone asks naturally.
          </li>
          <li>
            <a href="/docs/live-data">Live data & fetchData</a> — pull real
            data from real APIs at render time.
          </li>
        </OL>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/endpoints", label: "Workshop vs project endpoints" }}
        next={{ to: "/docs/data-tools", label: "Data tools + callTool()" }}
      />
    </DocPage>
  );
}
